
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { fadeInVariants } from '@/lib/animations';
import { Smile, Zap, Compass, Sparkles, Award, Music } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  iconComponent: React.ReactNode;
  earned: boolean;
  earnedAt?: string;
}

// Initial achievement data
const initialAchievements: Achievement[] = [
  {
    id: '1',
    title: 'Primeira Meditação',
    description: 'Completou sua primeira sessão de meditação',
    icon: '🧘‍♂️',
    iconComponent: <Smile className="text-brand-blue" />,
    earned: false,
  },
  {
    id: '2',
    title: 'Sequência de 3 Dias',
    description: 'Usou o app por 3 dias seguidos',
    icon: '🔥',
    iconComponent: <Zap className="text-orange-500" />,
    earned: false,
  },
  {
    id: '3',
    title: 'Explorador',
    description: 'Experimentou todos os tipos de exercícios',
    icon: '🧭',
    iconComponent: <Compass className="text-purple-500" />,
    earned: false,
  },
  {
    id: '4',
    title: 'Sequência de 7 Dias',
    description: 'Usou o app por uma semana inteira',
    icon: '✨',
    iconComponent: <Sparkles className="text-yellow-500" />,
    earned: false,
  },
  {
    id: '5',
    title: 'Colecionador de Calma',
    description: 'Completou 3 meditações da categoria Calma',
    icon: '🎵',
    iconComponent: <Music className="text-green-500" />,
    earned: false,
  },
  {
    id: '6',
    title: 'Mestre da Meditação',
    description: 'Completou pelo menos 5 meditações',
    icon: '🏆',
    iconComponent: <Award className="text-amber-500" />,
    earned: false,
  }
];

interface AchievementDashboardProps {
  userId?: string;
}

const AchievementDashboard = ({ userId }: AchievementDashboardProps) => {
  const [achievements, setAchievements] = useState<Achievement[]>(initialAchievements);
  const [completedMeditations, setCompletedMeditations] = useState<string[]>([]);
  const [currentStreak, setCurrentStreak] = useState(0);
  const monthDays = 30;
  
  useEffect(() => {
    // Fetch completed meditations for the current user
    const fetchUserData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        
        // Get all completed meditations
        const { data: meditationsData, error: meditationsError } = await supabase
          .from('completed_meditations')
          .select('*')
          .eq('user_id', session.user.id);
        
        if (meditationsError) {
          console.error('Error fetching completed meditations:', meditationsError);
          return;
        }
        
        // Process completed meditations data
        if (meditationsData && meditationsData.length > 0) {
          setCompletedMeditations(meditationsData.map(item => item.meditation_id));
          
          // Update achievements based on completed meditations
          const updatedAchievements = [...achievements];
          
          // First meditation achievement
          if (meditationsData.length >= 1) {
            const firstMeditationAchievement = updatedAchievements.find(a => a.id === '1');
            if (firstMeditationAchievement) {
              firstMeditationAchievement.earned = true;
              firstMeditationAchievement.earnedAt = new Date().toISOString().split('T')[0];
            }
          }
          
          // Master of Meditation (5 meditations)
          if (meditationsData.length >= 5) {
            const masterAchievement = updatedAchievements.find(a => a.id === '6');
            if (masterAchievement) {
              masterAchievement.earned = true;
              masterAchievement.earnedAt = new Date().toISOString().split('T')[0];
            }
          }
          
          // Check for calm meditations
          const calmMeditations = meditationsData.filter(med => med.meditation_id.startsWith('calm-'));
          if (calmMeditations.length >= 3) {
            const calmAchievement = updatedAchievements.find(a => a.id === '5');
            if (calmAchievement) {
              calmAchievement.earned = true;
              calmAchievement.earnedAt = new Date().toISOString().split('T')[0];
            }
          }
          
          // For now, we'll use completed meditations count as streak
          // In a real app, you'd implement proper streak tracking logic
          setCurrentStreak(Math.min(meditationsData.length, 7));
          
          // Update achievements state
          setAchievements(updatedAchievements);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, []);
  
  const progress = (currentStreak / monthDays) * 100;
  
  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={fadeInVariants}
    >
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Progresso & Conquistas</span>
            <span className="text-brand-blue">{currentStreak} dias</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Sequência atual</span>
                <span>{currentStreak}/{monthDays} dias</span>
              </div>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <Progress value={progress} className="h-2" />
              </motion.div>
            </div>
            
            <div className="pt-2">
              <h4 className="text-sm font-medium mb-3">Conquistas</h4>
              <div className="grid grid-cols-2 gap-2">
                {achievements.map((achievement) => (
                  <motion.div
                    key={achievement.id}
                    whileHover={{ y: -2, transition: { duration: 0.2 } }}
                    className={`p-3 rounded-lg border flex items-center space-x-3 ${
                      achievement.earned ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-100 opacity-60'
                    }`}
                  >
                    <div className="text-2xl">{achievement.iconComponent}</div>
                    <div>
                      <p className="text-sm font-medium">{achievement.title}</p>
                      {achievement.earned && (
                        <p className="text-xs text-gray-500">Obtido</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AchievementDashboard;
